import React from 'react';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogMedia,
  AlertDialogTitle,
  AlertDialogFooter,
  AlertDialogCancel,
} from '../../../components/ui/AlertDialog';
import { Button } from '../../../components/ui/Button2';
import { Icon } from '../../../components/ui/Icon';
import styles from './CascadeDeleteDialog.module.css';

/**
 * CascadeDeleteDialog — a destructive three-way choice shown when a record
 * has downstream dependents: Cancel / Delete Only This Record / Delete All
 * Related Records, above a scrollable list of the affected records.
 *
 * Built on the 2.0 AlertDialog primitive (portal, focus trap, Escape, scroll
 * lock, no backdrop-close come from the primitive). Only the body — the
 * warning banner + dependency list — keeps bespoke styling; that is unique
 * content, not chrome. The two delete buttons are plain Button2s (not
 * AlertDialogAction) so the dialog stays open showing "Deleting…" while the
 * async runs — the parent owns close via the controlled `isOpen` prop.
 */
const CascadeDeleteDialog = ({
  isOpen,
  onClose,
  onCascadeDelete,
  onDeleteOnly,
  recordId,
  recordType,
  dependentRecords = [],
  isDeleting = false,
}) => {
  const count = dependentRecords.length;

  return (
    <AlertDialog open={isOpen} onOpenChange={(open) => { if (!open && !isDeleting) onClose(); }}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogMedia>
            <Icon name="warning" tone="warning" size="lg" />
          </AlertDialogMedia>
          <AlertDialogTitle>Delete {recordType} {recordId}?</AlertDialogTitle>
        </AlertDialogHeader>

        <div className={styles.body}>
          <div className={styles.warningBox}>
            <Icon name="info" className={styles.warningBadge} />
            <div>
              <p className={styles.warningTitle}>
                This record has {count} dependent record{count !== 1 ? 's' : ''}
              </p>
              <p className={styles.warningText}>
                Deleting this record may affect other parts of your analysis.
              </p>
            </div>
          </div>

          <div className={styles.dependenciesSection}>
            <h3 className={styles.dependenciesTitle}>Related Records:</h3>
            <div className={styles.dependenciesList}>
              {dependentRecords.map((record, index) => (
                <div key={index} className={styles.dependencyCard}>
                  <div className={styles.dependencyHeader}>
                    <span className={styles.dependencyId}>{record.id}</span>
                  </div>
                  {record.description && (
                    <p className={styles.dependencyDescription}>{record.description}</p>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        <AlertDialogFooter>
          <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
          <Button
            variant="outline"
            className={styles.deleteOnlyBtn}
            onClick={onDeleteOnly}
            disabled={isDeleting}
          >
            {isDeleting ? 'Deleting…' : 'Delete Only This Record'}
          </Button>
          <Button
            variant="destructive"
            onClick={onCascadeDelete}
            disabled={isDeleting}
          >
            {isDeleting ? 'Deleting All…' : 'Delete All Related Records'}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default CascadeDeleteDialog;
